/// <amd-module name="Repos/Views/FilesHubActions/FilesHubActionsService" />
import * as React from "react";
import {
    GitAsyncOperationStatus,
    GitChange,
    GitForkSyncRequest,
    GitItemRequestData,
    GitPullRequest,
    GitPullRequestSearchCriteria,
    GitPush,
    GitRefUpdate,
    GitRepository,
    GitRepositoryRef,
    GitVersionOptions,
    GitVersionType,
    ItemContentType,
    PullRequestStatus,
    VersionControlChangeType,
    VersionControlRecursionType
} from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getShortCommitComment } from "Repos/Common/Util/Commit";
import { getFileName, getParentPath } from "Repos/Common/Util/File";
import { branchNameToRef, getRefNameFromBranchName, refToBranchName } from "Repos/Common/Util/Ref";
import { getVersionString } from "Repos/Common/Util/Version";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { IGitItem } from "Repos/Files/Contracts";
import { IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { IFilesHubViewService } from "Repos/Views/FilesHub/FilesHubViewService";
import * as Resources from "Repos/Views/FilesHubActions/Resources";
import { TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import { getTFSPageData, IProjectData } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { wait } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { FastPageSwitch, onClickFPS } from "VSS/Platform/FPS";
import { IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { toLookup } from "VSS/Platform/Util/Array";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IButtonProps } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { CommitChangesPanel, ICommitChangesContext, ICommitChangesDetails, PushCompleteListener } from "./Components/CommitChangesPanel";
import { ForkPanel, IForkDetails } from "./Components/ForkPanel";
import { NewFileDialog } from "./Components/NewFileDialog";
import { getGitClient } from "./RestClient/Git";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

/**
 * Service for performing actions on items in the Files hub
 */
export interface IFilesHubActionsService extends IVssService {
    clone(): void;
    commit(item: IGitItem, content: string): void;
    delete(item: IGitItem): void;
    fork(): void;
    newFile(parentItem: IGitItem): void;
    newFolder(parentItem: IGitItem): void;
    rename(item: IGitItem): void;
    upload(parentItem: IGitItem): void;
}

class FilesHubActionsService extends VssService implements IFilesHubActionsService {
    private repository: GitRepository;
    private project: IProjectData;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.repository = getGitRepository(this.pageContext)!;
        this.project = getTFSPageData(this.pageContext)!.project!;
    }

    public clone(): void {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return (
                <WrappedComponent
                    dependencies={["ms.vss-code-web.import-dependencies"]}
                    wrappedProps={{ onDismiss }}
                    wrappedType="ms.vss-code-web.panel-clone"
                />
            );
        });
    }

    public commit(item: IGitItem, content: string): void {
        this.handleFileAction(
            format(item.isNewItem ? Resources.AddedFileCommentFormat : Resources.UpdatedCommentFormat, item.name),
            undefined,
            undefined,
            undefined,
            details => {
                return this.performEditFile(item, details, content);
            },
            (
                details: ICommitChangesDetails,
                push: GitPush,
                version: string | undefined,
                error: Error | undefined,
                updatedPullRequest: GitPullRequest | undefined
            ) => {
                const filesService = this.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
                this.showCommitMessage(details, push, error, updatedPullRequest);

                // Clear the editing context's dirty state
                const itemContent = filesService.getSelectedItemContent();
                if (itemContent.value && itemContent.value.editContext) {
                    itemContent.value.editContext.setContent(itemContent.value.editContext.getContent(), true);
                }
                filesService.setEditMode(false);

                FastPageSwitch(this.pageContext, filesService.getItemUrl(item.path, { version }), false);
            }
        );
    }

    public delete(item: IGitItem): void {
        this.handleFileAction(
            format(Resources.DeletedCommentFormat, item.name),
            undefined,
            undefined,
            undefined,
            details => {
                return this.performDelete(item, details);
            },
            (
                details: ICommitChangesDetails,
                push: GitPush,
                version: string | undefined,
                error: Error | undefined,
                updatedPullRequest: GitPullRequest | undefined
            ) => {
                // Navigate to the parent item
                const filesService = this.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
                let parentPath = getParentPath(item.path);
                while (parentPath && filesService.getChildItems(parentPath).length < 2) {
                    // We may have deleted the last item in a folder, keep going up until we hit the root.
                    parentPath = getParentPath(parentPath);
                }

                this.showCommitMessage(details, push, error, updatedPullRequest);
                FastPageSwitch(this.pageContext, filesService.getItemUrl(parentPath || "/", { version }), false);
            }
        );
    }

    public fork(): void {
        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!;
        const userToken = pageData.user.displayName.replace(/ /g, ".").toLocaleLowerCase();

        const errorMessage = new ObservableValue("");
        const showOverlay = new ObservableValue(false);

        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const onDismissPanel = async (details: IForkDetails | undefined) => {
                if (details) {
                    showOverlay.value = true;

                    // Fork the repository
                    try {
                        await this.performFork(details, this.repository.defaultBranch);
                    } catch (ex) {
                        showOverlay.value = false;
                        errorMessage.value = ex.message;
                        return;
                    }

                    // Dimiss the panel
                    onDismiss();

                    // Navigate to the new repository
                    const routeService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
                    const newUrl = routeService.getFilesUrl(details.repositoryName, details.project.name);
                    FastPageSwitch(this.pageContext, newUrl, true);
                } else {
                    onDismiss();
                }
            };

            return (
                <ForkPanel
                    defaultBranchName={refToBranchName(this.repository.defaultBranch)}
                    initialProject={{ id: this.project.id, name: this.project.name } as TeamProjectReference}
                    initialRepositoryName={this.repository.name}
                    errorMessage={errorMessage}
                    onDismiss={onDismissPanel}
                    showOverlay={showOverlay}
                    userToken={userToken}
                />
            );
        });
    }

    public newFile(parentItem: IGitItem): void {
        this.handleNewFile(parentItem, false);
    }

    public newFolder(parentItem: IGitItem): void {
        this.handleNewFile(parentItem, true);
    }

    public rename(item: IGitItem): void {
        const itemName = getFileName(item.path);
        this.handleFileAction(
            format(Resources.RenameCommentFormat, itemName, itemName),
            itemName,
            undefined,
            undefined,
            details => {
                return this.performRename(item, details);
            },
            (
                details: ICommitChangesDetails,
                push: GitPush,
                version: string | undefined,
                error: Error | undefined,
                updatedPullRequest: GitPullRequest | undefined
            ) => {
                const filesService = this.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
                const parentPath = getParentPath(item.path) || "/";
                const newPath = combineUrlPaths(parentPath, details.renameTo!);

                this.showCommitMessage(details, push, error, updatedPullRequest);
                FastPageSwitch(this.pageContext, filesService.getItemUrl(newPath, { version }), false);
            }
        );
    }

    public upload(parentItem: IGitItem, initialFiles?: FileList): void {
        this.handleFileAction(
            "",
            undefined,
            parentItem,
            initialFiles,
            details => {
                return this.performUpdate(parentItem, details);
            },
            (
                details: ICommitChangesDetails,
                push: GitPush,
                version: string | undefined,
                error: Error | undefined,
                updatedPullRequest: GitPullRequest | undefined
            ) => {
                const filesService = this.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
                this.showCommitMessage(details, push, error, updatedPullRequest);
                FastPageSwitch(this.pageContext, filesService.getItemUrl(parentItem.path, { version }), false);
            }
        );
    }

    private handleNewFile(parentItem: IGitItem, isFolder: boolean): void {
        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const onDismissDialog = async (name: string | undefined) => {
                if (name) {
                    // Make sure that the path doesn't already exist
                    const path = combineUrlPaths(parentItem.path, name);

                    const filesService = this.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");

                    // Dimiss the panel
                    onDismiss();

                    // Navigate to the new path
                    const newUrl = filesService.getItemUrl(path) + "&createIfNew=true";
                    FastPageSwitch(this.pageContext, newUrl, true);
                } else {
                    onDismiss();
                }
            };

            return <NewFileDialog createFolder={isFolder} onDismiss={onDismissDialog} parentPath={parentItem.path} />;
        });
    }

    private handleFileAction(
        initialComment: string,
        renameFrom: string | undefined,
        uploadTo: IGitItem | undefined,
        initialUploadFiles: FileList | undefined,
        performAction: (details: ICommitChangesDetails) => Promise<GitPush>,
        postActionNavigate: (
            details: ICommitChangesDetails,
            push: GitPush,
            newVersion: string | undefined,
            error: Error | undefined,
            updatedPullRequest: GitPullRequest | undefined
        ) => void
    ): void {
        const filesService = this.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
        const errorMessage = new ObservableValue("");
        const showOverlay = new ObservableValue(false);
        const version = filesService.getVersion();

        const listeners: PushCompleteListener[] = [];
        const context: ICommitChangesContext = {
            addPushCompleteListener: (listener: PushCompleteListener): void => {
                listeners.push(listener);
            }
        };

        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const onDismissPanel = async (details: ICommitChangesDetails | undefined) => {
                if (details) {
                    errorMessage.value = "";
                    showOverlay.value = true;

                    let result: GitPush;
                    try {
                        result = await performAction(details);
                    } catch (ex) {
                        showOverlay.value = false;
                        errorMessage.value = ex.message;
                        return;
                    }

                    let newBranchName: string | undefined;
                    if (version.versionType !== GitVersionType.Branch || version.version !== details.branch) {
                        newBranchName = details.branch;
                    }

                    const promises: Promise<void>[] = [];
                    for (const listener of listeners) {
                        const postPushResult = listener(result, newBranchName);
                        if (postPushResult && postPushResult.then) {
                            promises.push(postPushResult);
                        }
                    }

                    let error: Error | undefined;
                    if (promises.length) {
                        try {
                            await Promise.all(promises);
                        } catch (ex) {
                            error = ex;
                        }
                    }

                    // Dimiss the panel
                    onDismiss();

                    // Navigate to the new path
                    if (details.createPullRequest) {
                        const routeService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
                        const pullRequestUrl = routeService.getPullRequestCreateUrl(
                            this.repository.name,
                            this.project.name,
                            details.branch,
                            version.versionType === GitVersionType.Branch ? version.version : undefined,
                            this.repository.id,
                            this.repository.id
                        );
                        filesService.setEditMode(false);
                        FastPageSwitch(this.pageContext, pullRequestUrl, true);
                    } else {
                        let newVersion: string | undefined;
                        let updatedPullRequest: GitPullRequest | undefined;
                        if (newBranchName) {
                            const newDescriptor = {
                                version: newBranchName,
                                versionType: GitVersionType.Branch,
                                versionOptions: GitVersionOptions.None
                            };
                            newVersion = getVersionString(newDescriptor);
                        } else if (version.version !== refToBranchName(this.repository.defaultBranch)) {
                            // Pushing to the current branch, check to see if an existing pull request exists for it
                            const client = getGitClient(this.pageContext);
                            const result = await client.getPullRequests(
                                this.repository.id,
                                {
                                    repositoryId: this.repository.id,
                                    sourceRefName: getRefNameFromBranchName(version.version),
                                    sourceRepositoryId: this.repository.id,
                                    status: PullRequestStatus.Active
                                } as GitPullRequestSearchCriteria,
                                this.project.id
                            );
                            updatedPullRequest = result[0];
                        }
                        postActionNavigate(details, result, newVersion, error, updatedPullRequest);
                    }
                } else {
                    onDismiss();
                }
            };

            return (
                <CommitChangesPanel
                    comment={initialComment}
                    commitChangesContext={context}
                    errorMessage={errorMessage}
                    initialFiles={initialUploadFiles}
                    onDismiss={onDismissPanel}
                    renameFrom={renameFrom}
                    showOverlay={showOverlay}
                    uploadExistingItems={uploadTo && filesService.getChildItems(uploadTo.path)}
                    uploadTo={uploadTo && uploadTo.path}
                    version={version}
                />
            );
        });
    }

    private async performDelete(item: IGitItem, details: ICommitChangesDetails): Promise<GitPush> {
        return this.performPush(item, details, [
            {
                changeType: VersionControlChangeType.Delete,
                item: {
                    path: item.path
                }
            } as GitChange
        ]);
    }

    private async performEditFile(item: IGitItem, details: ICommitChangesDetails, content: string): Promise<GitPush> {
        return this.performPush(item, details, [
            {
                changeType: item.isNewItem ? VersionControlChangeType.Add : VersionControlChangeType.Edit,
                item: {
                    path: item.path
                },
                newContent: {
                    content: content,
                    contentType: ItemContentType.RawText
                }
            } as GitChange
        ]);
    }

    private async performRename(item: IGitItem, details: ICommitChangesDetails): Promise<GitPush> {
        return this.performPush(item, details, [
            {
                changeType: VersionControlChangeType.Rename,
                item: {
                    path: combineUrlPaths(getParentPath(item.path) || "/", details.renameTo!)
                },
                sourceServerItem: item.path
            } as GitChange
        ]);
    }

    private async performUpdate(parentItem: IGitItem, details: ICommitChangesDetails): Promise<GitPush> {
        const filesService = this.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
        const existingItems = toLookup(filesService.getChildItems(parentItem.path).value, i => i.name.toLocaleLowerCase());

        const changes: GitChange[] = [];
        for (const file of details.files!) {
            const existingItem = existingItems[file.name.toLocaleLowerCase()];
            changes.push({
                changeType: existingItem && !existingItem.isFolder ? VersionControlChangeType.Edit : VersionControlChangeType.Add,
                item: {
                    path: existingItem ? existingItem.path : combineUrlPaths(parentItem.path, file.name)
                },
                newContent: {
                    content: file.content!,
                    contentType: ItemContentType.Base64Encoded
                }
            } as GitChange);
        }
        return this.performPush(parentItem, details, changes);
    }

    private async performPush(item: IGitItem, details: ICommitChangesDetails, changes: GitChange[]): Promise<GitPush> {
        let oldObjectId = item.commitId;
        if (item.isNewItem) {
            const filesService = this.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
            oldObjectId = filesService.getRootItem().commitId;
        }

        const push = {
            commits: [
                {
                    changes,
                    comment: details.comment
                }
            ],
            refUpdates: [
                {
                    name: "refs/heads/" + details.branch,
                    oldObjectId
                } as GitRefUpdate
            ]
        } as GitPush;

        const client = getGitClient(this.pageContext);

        let result: GitPush;
        try {
            result = await client.createPush(push, this.repository.id);
        } catch (ex) {
            if (ex.status === 409) {
                // The branch has been updated since this item was first loaded, get the most-recent version of the item
                // to see if it has changed itself since then
                const filesService = this.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");

                let refreshedItemObjectId: string | undefined;
                let currentCommitId: string | undefined;

                // For new files, fetch the root item to get the most-recent commit id of this branch
                if (item.isNewItem) {
                    const items = await client.getItemsBatch(
                        {
                            itemDescriptors: [
                                {
                                    path: "/",
                                    recursionLevel: VersionControlRecursionType.None,
                                    ...filesService.getVersion()
                                }
                            ]
                        } as GitItemRequestData,
                        this.repository.id,
                        this.project.id
                    );
                    currentCommitId = items[0][0].commitId;
                }

                // Fetch the item itself to make sure it hasn't changed from what was
                // originally edited
                try {
                    const items = await client.getItemsBatch(
                        {
                            itemDescriptors: [
                                {
                                    path: item.path,
                                    recursionLevel: VersionControlRecursionType.None,
                                    ...filesService.getVersion()
                                }
                            ]
                        } as GitItemRequestData,
                        this.repository.id,
                        this.project.id
                    );
                    refreshedItemObjectId = items[0][0].objectId;
                    currentCommitId = items[0][0].commitId;
                } catch {
                    // Item does not exist -- it may have been deleted or it could be a new file
                }

                if (refreshedItemObjectId === item.objectId && currentCommitId) {
                    // Even though the branch was updated, this item was not touched.
                    // Retry the push using the most recent commit id
                    push.refUpdates[0].oldObjectId = currentCommitId;
                    result = await client.createPush(push, this.repository.id);
                } else {
                    // Throw the conflict error
                    throw ex;
                }
            } else {
                throw ex;
            }
        }

        return result;
    }

    private async performFork(details: IForkDetails, defaultRefName: string): Promise<void> {
        const client = getGitClient(this.pageContext);
        const newRepository = await client.createRepository(
            {
                name: details.repositoryName,
                parentRepository: { id: this.repository.id, project: { id: this.project.id } } as GitRepositoryRef,
                project: { id: details.project.id, name: details.project.name } as TeamProjectReference
            },
            details.project.id,
            details.includeAllBranches ? undefined : defaultRefName
        );

        const syncRequests = await client.getForkSyncRequests(newRepository.id, details.project.id);
        return this.waitForForkResult(syncRequests[0], newRepository.id, details.project.id);
    }

    private async waitForForkResult(syncRequest: GitForkSyncRequest, repositoryId: string, projectId: string): Promise<void> {
        if (syncRequest.status === GitAsyncOperationStatus.Completed) {
            return;
        } else if (syncRequest.status === GitAsyncOperationStatus.InProgress || syncRequest.status === GitAsyncOperationStatus.Queued) {
            // Wait 1 second before trying again
            await wait(1000);

            // Fetch the new sync status
            const client = getGitClient(this.pageContext);
            const syncResult = await client.getForkSyncRequest(repositoryId, syncRequest.operationId, projectId);

            // Check the new result
            return this.waitForForkResult(syncResult, repositoryId, projectId);
        } else {
            throw new Error(syncRequest.detailedStatus.errorMessage);
        }
    }

    private async showCommitMessage(
        details: ICommitChangesDetails,
        push: GitPush,
        error: Error | undefined,
        updatedPullRequest: GitPullRequest | undefined
    ) {
        const filesService = this.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
        const commit = push.commits[0];

        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const entry = await settingsService.getEntriesAsync("", SettingsUserScope.Me, "Repository", this.repository.id);

        const featureFlagEnabled = isFeatureFlagEnabled(this.pageContext, "WebAccess.VersionControl.PullRequests.UpdateTargetBranches2", false);

        let targetBranch = filesService.getVersion().version;
        if (targetBranch === details.branch) {
            if (!featureFlagEnabled && entry && entry["Branches.Compare"]) {
                targetBranch = refToBranchName(entry["Branches.Compare"]);
            }
            else if(this.repository.defaultBranch) {
                targetBranch = refToBranchName(this.repository.defaultBranch);
            }
        }

        const routingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const commitUrl = routingService.getCommitUrl(this.repository.name, this.project.name, commit.commitId, branchNameToRef(details.branch));
        const commitLink = (
            <span>
                <Icon iconName="BranchCommit" className="margin-right-4" />
                <FPSLink href={commitUrl} removeUnderline={true}>{commit.commitId.substr(0, 8) + ": " + getShortCommitComment(commit.comment)}</FPSLink>
            </span>
        );

        let buttons: IButtonProps[] | undefined;
        let message: JSX.Element;
        if (updatedPullRequest) {
            const pullRequestUrl = routingService.getPullRequestUrl(this.repository.name, this.project.name, updatedPullRequest.pullRequestId);
            message = (
                <FormatComponent elementType="span" format={Resources.CommitPushedWithPRMessageFormat}>
                    {commitLink}
                    <span>
                        <Icon iconName="BranchPullRequest" className="margin-right-4" />
                        <FPSLink href={pullRequestUrl} removeUnderline={true}>{updatedPullRequest.title}</FPSLink>
                    </span>
                </FormatComponent>
            );
        } else {
            message = (
                <FormatComponent elementType="span" format={Resources.CommitPushedMessageFormat}>
                    {commitLink}
                </FormatComponent>
            );

            const pullRequestUrl = routingService.getPullRequestCreateUrl(
                this.repository.name,
                this.project.name,
                details.branch,
                targetBranch,
                this.repository.id,
                this.repository.id
            );

            buttons = [
                {
                    href: pullRequestUrl,
                    text: Resources.CreateAPullRequest,
                    onClick: event => {
                        onClickFPS(this.pageContext, pullRequestUrl, true, event);
                    }
                }
            ];
        }

        const messagesService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
        messagesService.showMessage(
            {
                buttons,
                renderMessage: () => (
                    <>
                        {message}
                        {error ? <span>{error.message}</span> : null}
                    </>
                ),
                severity: error ? MessageCardSeverity.Warning : MessageCardSeverity.Info
            },
            true
        );
    }
}

Services.add("IFilesHubActionsService", { serviceFactory: FilesHubActionsService });
